import React, {useEffect, useRef, useState} from 'react';
import {FaCalendarAlt, FaCheck, FaEdit, FaPlus, FaTimes, FaTrashAlt} from 'react-icons/fa';
import AuthenticatedLayout from '../authenticatedLayout/AuthenticatedLayout';
import './EmployeeManagement.css';
import {USER_ENDPOINTS} from "../../Constants";
import '../../App.css'

const EmployeeManagement = () => {
    const [employees, setEmployees] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [employeeToDelete, setEmployeeToDelete] = useState(null);
    const [currentEmployee, setCurrentEmployee] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        salary: '',
        hourRate: '',
        startDate: '',
        endDate: '',
        isActive: true,
        annualVacationDays: '',
        remainingVacationDays: ''
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const isMobile = windowWidth < 769; // Mobile breakpoint

    // Refs for modal elements
    const modalRef = useRef(null);
    const deleteModalRef = useRef(null);

    // Animation duration in ms (should match CSS animation time)
    const animationDuration = 300;

    const [formError, setFormError] = useState('');

    // Track window resize for responsive design
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch employees on component mount
    useEffect(() => {
        fetchEmployees();
    }, []);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(USER_ENDPOINTS.EMPLOYEE, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const data = await response.json();

            // If isActive is not defined in the API response, determine it from endDate
            const processedData = data.map(emp => ({
                ...emp,
                isActive: emp.isActive !== undefined ? emp.isActive :
                    (!emp.endDate || new Date(emp.endDate) > new Date())
            }));

            setEmployees(processedData);
            setError(null);
        } catch (err) {
            console.error('Error fetching employees:', err);
            setError('Nu s-au putut încărca datele angajaților. Vă rugăm încercați din nou.');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (employee = null) => {
        if (employee) {
            // Edit mode
            setCurrentEmployee(employee);
            setFormData({
                name: employee.name,
                phone: employee.phone,
                salary: employee.salary || '',
                hourRate: employee.hourRate || '',
                startDate: employee.startDate ? employee.startDate.substring(0, 10) : '',
                endDate: employee.endDate ? employee.endDate.substring(0, 10) : '',
                isActive: employee.isActive !== undefined ? employee.isActive : true,
                annualVacationDays: employee.annualVacationDays || '',
                remainingVacationDays: employee.remainingVacationDays || ''
            });
        } else {
            // Add mode
            setCurrentEmployee(null);
            setFormData({
                name: '',
                phone: '',
                salary: '',
                hourRate: '',
                startDate: '',
                endDate: '',
                isActive: true,
                annualVacationDays: '',
                remainingVacationDays: ''
            });
        }
        setIsClosing(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setIsClosing(true);
        setFormError('');

        // Actually close the modal after animation completes
        setTimeout(() => {
            setShowModal(false);
            setCurrentEmployee(null);
            setIsClosing(false);
        }, animationDuration);
    };

    const handleInputChange = (e) => {
        const {name, value, type} = e.target;

        if (type === 'radio') {
            const isActive = name === 'statusActive';

            // Clear the endDate if switching to active
            if (isActive) {
                setFormData(prev => ({
                    ...prev,
                    isActive: true,
                    endDate: '' // Clear end date when setting active
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    isActive: false
                }));
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const validateRomanianPhone = (phone) => {
        // Remove all non-digit characters
        const cleanPhone = phone.replace(/\D/g, '');

        // Romanian mobile number patterns:
        // 07XXXXXXXX (10 digits starting with 07)
        // +4070XXXXXXXX (international format)
        // 4070XXXXXXXX (without +)

        if (cleanPhone.length === 10 && cleanPhone.startsWith('07')) {
            return true;
        }

        if (cleanPhone.length === 12 && cleanPhone.startsWith('4070')) {
            return true;
        }

        return false;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        // Validare număr de telefon
        if (!validateRomanianPhone(formData.phone)) {
            setFormError('Numărul de telefon nu este valid. Folosiți formatul 07XXXXXXXX.');
            return;
        }

        // Validate that endDate is provided if isActive is false
        if (!formData.isActive && !formData.endDate) {
            setFormError('Vă rugăm să completați Data demisiei pentru un angajat inactiv.');
            return;
        }

        try {
            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('No authentication token found');
            }

            // Prepare payload - if active, ensure endDate is null
            const payload = {
                ...formData,
                salary: parseFloat(formData.salary),
                hourRate: formData.hourRate ? parseFloat(formData.hourRate) : null,
                endDate: formData.isActive ? null : formData.endDate, // Ensure endDate is null when active
                annualVacationDays: parseFloat(formData.annualVacationDays) || 0,
                remainingVacationDays: parseFloat(formData.remainingVacationDays) || 0
            };

            let response;

            if (currentEmployee) {
                // Update existing employee
                response = await fetch(`${USER_ENDPOINTS.EMPLOYEE}/${currentEmployee.id}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        ...payload,
                        id: currentEmployee.id
                    })
                });
            } else {
                // Create new employee
                response = await fetch(USER_ENDPOINTS.EMPLOYEE, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Refresh employee list
            fetchEmployees();
            handleCloseModal();
        } catch (err) {
            console.error('Error saving employee:', err);
            setFormError('A apărut o eroare la salvarea datelor. Vă rugăm încercați din nou.');
        }
    };

    // Open delete confirmation modal
    const handleOpenDeleteModal = (employee) => {
        setEmployeeToDelete(employee);
        setIsClosing(false);
        setShowDeleteModal(true);
    };

    // Close delete confirmation modal
    const handleCloseDeleteModal = () => {
        // Start closing animation
        setIsClosing(true);

        // Actually close the modal after animation completes
        setTimeout(() => {
            setShowDeleteModal(false);
            setEmployeeToDelete(null);
            setIsClosing(false);
        }, animationDuration);
    };

    // Handle delete confirmation
    const confirmDelete = async () => {
        if (!employeeToDelete) return;

        try {
            const token = localStorage.getItem('token');

            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await fetch(`${USER_ENDPOINTS.EMPLOYEE}/${employeeToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            // Refresh employee list
            fetchEmployees();
            handleCloseDeleteModal();
        } catch (err) {
            console.error('Error deleting employee:', err);
            setFormError('A apărut o eroare la ștergerea angajatului. Vă rugăm încercați din nou.');
            handleCloseDeleteModal();
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('ro-RO');
    };

    // Format currency for display
    const formatCurrency = (value) => {
        if (!value && value !== 0) return '';
        return new Intl.NumberFormat('ro-RO', {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(value);
    };

    // Render the mobile card view for each employee
    const renderMobileCard = (employee) => {
        return (
            <div key={employee.id} className={`employee-card ${!employee.isActive ? 'inactive' : ''}`}>
                <div className="card-header">
                    <div className="employee-name">{employee.name}</div>
                    <div
                        className={`status-badge ${employee.isActive ? 'status-badge-active' : 'status-badge-inactive'}`}>
                        <span
                            className={`status-indicator ${employee.isActive ? 'status-active' : 'status-inactive'}`}></span>
                        <span>{employee.isActive ? 'Activ' : 'Inactiv'}</span>
                    </div>
                </div>

                <div className="card-info">
                    <FaCalendarAlt className="calendar-icon"/> {formatDate(employee.startDate)}
                </div>

                <div className="card-info">
                    {employee.phone}
                </div>

                <div className="card-info">
                    <strong>Salariu:</strong> {formatCurrency(employee.salary)}
                </div>

                <div className="card-info">
                    <strong>Preț oră:</strong> {formatCurrency(employee.hourRate)}
                </div>

                <div className="card-info">
                    <strong>Libere anuale:</strong> {employee.annualVacationDays}
                </div>

                <div className="card-info">
                    <strong>Libere rămase:</strong> {employee.remainingVacationDays}
                </div>

                {!employee.isActive && employee.endDate && (
                    <div className="card-info">
                        <strong>Data demisiei:</strong> {formatDate(employee.endDate)}
                    </div>
                )}

                <div className="card-actions">
                    <button
                        className="card-action-button card-edit-button"
                        onClick={() => handleOpenModal(employee)}
                        aria-label="Edit"
                    >
                        <FaEdit/>
                    </button>
                    <button
                        className="card-action-button card-delete-button"
                        onClick={() => handleOpenDeleteModal(employee)}
                        aria-label="Delete"
                    >
                        <FaTrashAlt/>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <AuthenticatedLayout>
            <div className="employee-container">
                <div className="header-container">
                    <h1 className="page-title">Angajați</h1>
                    <button
                        className="add-button"
                        onClick={() => handleOpenModal()}
                    >
                        <FaPlus/> Adaugă angajat
                    </button>
                </div>

                {loading ? (
                    <div className="loading-message">Se încarcă...</div>
                ) : error ? (
                    <div className="error-message">{error}</div>
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="employee-table-container">
                            <table className="employee-table">
                                <thead>
                                <tr>
                                    <th>STARE</th>
                                    <th>DATA ANGAJĂRII</th>
                                    <th>NUME</th>
                                    <th>TELEFON</th>
                                    <th>SALARIU</th>
                                    <th>PREȚ ORĂ</th>
                                    <th>LiBERE ANUALE</th>
                                    <th>LiBERE RĂMASE</th>
                                    <th>DATA DEMISIEI</th>
                                    <th>ACȚIUNI</th>
                                </tr>
                                </thead>
                                <tbody>
                                {employees.map((employee) => {
                                    return (
                                        <tr key={employee.id}>
                                            <td>
                                                <div
                                                    className={`status-indicator ${employee.isActive ? 'status-active' : 'status-inactive'}`}></div>
                                            </td>
                                            <td>
                                                <div className="date-cell">
                                                    {formatDate(employee.startDate)} <FaCalendarAlt
                                                    className="calendar-icon"/>
                                                </div>
                                            </td>
                                            <td>{employee.name}</td>
                                            <td>{employee.phone}</td>
                                            <td>{formatCurrency(employee.salary)}</td>
                                            <td>{formatCurrency(employee.hourRate)}</td>
                                            <td>{employee.annualVacationDays || 0}</td>
                                            <td>{employee.remainingVacationDays || 0}</td>
                                            <td>{employee.isActive ? '' : formatDate(employee.endDate)}</td>
                                            <td>
                                                <button
                                                    className="action-button edit-button"
                                                    onClick={() => handleOpenModal(employee)}
                                                >
                                                    <FaEdit/>
                                                </button>
                                                <button
                                                    className="action-button delete-button"
                                                    onClick={() => handleOpenDeleteModal(employee)}
                                                >
                                                    <FaTrashAlt/>
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="employee-cards">
                            {employees.map(renderMobileCard)}
                        </div>
                    </>
                )}

                {/* Add/Edit Employee Modal */}
                {showModal && (
                    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`}>
                        <div className="modal-content" ref={modalRef}>
                            <div className="modal-header">
                                <h2 className="modal-title">{currentEmployee ? 'Editare angajat' : 'Adaugă angajat'}</h2>
                                <button className="close-button" onClick={handleCloseModal}>
                                    <FaTimes/>
                                </button>
                            </div>

                            <div className="modal-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="nume">Nume</label>
                                        <input
                                            id="nume"
                                            name="name"
                                            type="text"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="telefon">Telefon</label>
                                        <input
                                            id="telefon"
                                            name="phone"
                                            type="text"
                                            value={formData.phone}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            placeholder="0740123456"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="salariu">Salariu</label>
                                        <input
                                            id="salariu"
                                            name="salary"
                                            type="number"
                                            step="0.01"
                                            value={formData.salary}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="pretOra">Preț oră</label>
                                        <input
                                            id="pretOra"
                                            name="hourRate"
                                            type="number"
                                            step="0.01"
                                            value={formData.hourRate}
                                            onChange={handleInputChange}
                                            className="form-control"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="dataStart">Data angajării</label>
                                        <input
                                            id="dataStart"
                                            name="startDate"
                                            type="date"
                                            value={formData.startDate}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="annualVacationDays">Libere anuale</label>
                                        <input
                                            id="annualVacationDays"
                                            name="annualVacationDays"
                                            type="number"
                                            step="0.1"
                                            value={formData.annualVacationDays}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label" htmlFor="remainingVacationDays">Libere
                                            rămase</label>
                                        <input
                                            id="remainingVacationDays"
                                            name="remainingVacationDays"
                                            type="number"
                                            step="0.1"
                                            value={formData.remainingVacationDays}
                                            onChange={handleInputChange}
                                            className="form-control"
                                            required
                                        />
                                    </div>

                                    <div className="status-radio-group">
                                        <label className="status-radio-label">
                                            <input
                                                type="radio"
                                                name="statusActive"
                                                checked={formData.isActive === true}
                                                onChange={handleInputChange}
                                                className="status-radio-input-active"
                                            />
                                            <span className="status-radio-text">Activ</span>
                                        </label>
                                        <label className="status-radio-label">
                                            <input
                                                type="radio"
                                                name="statusInactive"
                                                checked={formData.isActive === false}
                                                onChange={handleInputChange}
                                                className="status-radio-input-inactive"
                                            />
                                            <span className="status-radio-text">Inactiv</span>
                                        </label>
                                    </div>

                                    {/* Only show Data demisiei field if inactive is selected */}
                                    {!formData.isActive && (
                                        <div className="form-group">
                                            <label className="form-label" htmlFor="dataEnd">Data demisiei</label>
                                            <input
                                                id="dataEnd"
                                                name="endDate"
                                                type="date"
                                                value={formData.endDate}
                                                onChange={handleInputChange}
                                                className="form-control"
                                                required={!formData.isActive}
                                            />
                                        </div>
                                    )}
                                    {formError && (
                                        <div style={{
                                            color: '#dc3545',
                                            backgroundColor: '#f8d7da',
                                            border: '1px solid #f5c6cb',
                                            borderRadius: '4px',
                                            padding: '0.75rem 1rem',
                                            margin: '1rem 0',
                                            fontSize: '0.875rem'
                                        }}>
                                            {formError}
                                        </div>
                                    )}
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="cancel-button"
                                            onClick={handleCloseModal}
                                        >
                                            <FaTimes/> Anulează
                                        </button>
                                        <button
                                            type="submit"
                                            className="save-button"
                                        >
                                            <FaCheck/> {currentEmployee ? 'Salvează' : 'Adaugă'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteModal && employeeToDelete && (
                    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`}>
                        <div className="modal-content delete-modal" ref={deleteModalRef}>
                            <div className="modal-header delete-modal-header">
                                <h2 className="modal-title">Confirmă ștergerea</h2>
                                <button className="close-button" onClick={handleCloseDeleteModal}>
                                    <FaTimes/>
                                </button>
                            </div>

                            <div className="modal-body delete-modal-body">
                                <div className="delete-warning">
                                    <p className="delete-message">
                                        Sunteți sigur că doriți să ștergeți
                                        angajatul <strong>{employeeToDelete.name}</strong>? Toate datele despre acesta
                                        vor fi sterse, inclusiv platile anterioare. Recomandam dezactivarea acestuia.
                                    </p>
                                    <p className="delete-warning-text">
                                        Această acțiune nu poate fi anulată.
                                    </p>
                                </div>

                                <div className="modal-footer delete-modal-footer">
                                    <button
                                        type="button"
                                        className="save-button"
                                        onClick={handleCloseDeleteModal}
                                    >
                                        <FaTimes/> Nu
                                    </button>
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={confirmDelete}
                                    >
                                        <FaTrashAlt/> Da
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AuthenticatedLayout>
    );
};

export default EmployeeManagement;